/**
 * INITIAL SETUP: Run when the DOM content is loaded.
 * - Disables the #ship-confirm button
 * - Fetches the cart and renders shipping options for each logmat group
 */
document.addEventListener("DOMContentLoaded", function() {
  console.log("[Script] Page loaded. Starting initial cart processing.");

  // Disable the #ship-confirm button initially
  const shipConfirmBtn = document.getElementById("ship-confirm");
  if (shipConfirmBtn) {
    shipConfirmBtn.disabled = true;
    console.log("[Script] Disabled #ship-confirm button on page load.");
  } else {
    console.warn("[Script] #ship-confirm button not found.");
  }

  fetchCartData().then(cart => {
    window.cart = cart;
    console.log("[Script] Cart data fetched:", cart);

    // Extract initial logmat mapping from the DOM (from .cart-line-item elements)
    const logmatMapping = extractLogmatFromCartDOM();
    console.log("[Script] Initial logmat mapping from DOM:", logmatMapping);

    // Process each logmat group (container elements with class .logmat-group)
    document.querySelectorAll(".logmat-group").forEach(group => {
      const logmatId = group.getAttribute("data-logmat");
      if (!logmatId) return;

      // Compute the total for this logmat using both the initial mapping and dynamic cart data
      const logmatTotal = computeLogmatTotal(logmatId, logmatMapping);
      console.log("[Processing] Computed logmat total for logmat", logmatId, ":", logmatTotal);

      // Determine shipping options for this group based on the total, customer metafields, and warehouse data
      const options = getShippingOptionsForGroup(logmatId, logmatTotal);
      console.log("[Processing] Shipping options for logmat", logmatId, ":", options);

      // Render the shipping options as radio buttons using our templated snippets
      renderRadioButtonsForGroup(group, options);
    });

    // Validate selections so that #ship-confirm is enabled only when every group has a selection
    validateShippingSelections();
    console.log("[Script] Initial shipping options rendering complete.");
  });
});

/**
 * LISTENER: Re-run shipping options rendering on cart updates.
 * This will re-fetch the cart, re-render options, and disable the #ship-confirm button.
 */
document.addEventListener("cart:updated", function() {
  console.log("[cart:updated] Event detected! Recalculating logmat totals and re-rendering shipping options.");

  fetchCartData().then(cart => {
    window.cart = cart;
    console.log("[cart:updated] Updated cart data:", cart);

    // Disable the ship-confirm button since selections are cleared on update
    const shipConfirmBtn = document.getElementById("ship-confirm");
    if (shipConfirmBtn) {
      shipConfirmBtn.disabled = true;
      console.log("[cart:updated] Disabled #ship-confirm due to cart update.");
    }

    // Re-render shipping options for each logmat group
    document.querySelectorAll(".logmat-group").forEach(group => {
      const logmatId = group.getAttribute("data-logmat");
      if (!logmatId) return;

      const logmatTotal = computeLogmatTotal(logmatId);
      console.log("[cart:updated] Computed logmat total for logmat", logmatId, ":", logmatTotal);

      const options = getShippingOptionsForGroup(logmatId, logmatTotal);
      console.log("[cart:updated] Shipping options for logmat", logmatId, ":", options);

      renderRadioButtonsForGroup(group, options);
    });

    validateShippingSelections();
    console.log("[cart:updated] Re-rendering of shipping options complete.");
  });
});

/**
 * FETCH CART DATA: Retrieves the latest cart data from Shopify.
 */
function fetchCartData() {
  console.log("[fetchCartData] Fetching latest cart data...");
  return fetch('/cart.js')
    .then(response => response.json())
    .then(cartData => {
      console.log("[fetchCartData] Cart data retrieved:", cartData);
      return cartData;
    })
    .catch(error => {
      console.error("[fetchCartData] Error fetching cart:", error);
      return {};
    });
}

/**
 * EXTRACT LOGMAT FROM DOM: Reads the logmat value from each .cart-line-item element.
 * This is used for initial load to assign a logmat group based on DOM data.
 */
function extractLogmatFromCartDOM() {
  console.log("[extractLogmatFromCartDOM] Extracting logmat data from DOM.");
  let logmatMap = {};

  document.querySelectorAll(".cart-line-item").forEach(item => {
    const logmatId = item.getAttribute("data-logmat");
    const variantId = item.getAttribute("data-variant-id");
    const linePrice = parseInt(item.getAttribute("data-line-price") || "0", 10);

    if (!logmatId || !variantId) {
      console.warn("[extractLogmatFromCartDOM] Skipping item without logmat or variantId:", item);
      return;
    }

    if (!logmatMap[logmatId]) {
      logmatMap[logmatId] = 0;
    }
    logmatMap[logmatId] += linePrice;
    console.log(`[extractLogmatFromCartDOM] For logmat ${logmatId}, added line price: ${linePrice}`);
  });

  console.log("[extractLogmatFromCartDOM] Final logmat mapping:", logmatMap);
  return logmatMap;
}

/**
 * COMPUTE LOGMAT TOTAL: Computes the total for a given logmat group.
 * On initial load, it uses the DOM-extracted mapping; then, it adds in dynamic cart data.
 */
function computeLogmatTotal(logmatId, initialLogmatMapping = {}) {
  console.log("[computeLogmatTotal] Called for logmatId:", logmatId);
  let logmatTotals = { ...initialLogmatMapping };

  if (window.cart && window.cart.items) {
    window.cart.items.forEach(item => {
      const variantId = item.variant_id.toString();
      // Find the corresponding .cart-line-item element to extract its logmat value
      const matchingCartItem = document.querySelector(`.cart-line-item[data-variant-id="${variantId}"]`);
      if (matchingCartItem) {
        const logmatValue = matchingCartItem.getAttribute("data-logmat");
        if (!logmatValue) return;
        if (!logmatTotals[logmatValue]) {
          logmatTotals[logmatValue] = 0;
        }
        logmatTotals[logmatValue] += item.final_line_price;
      }
    });
  }

  console.log("[computeLogmatTotal] Computed totals:", logmatTotals);
  return logmatTotals[logmatId] || 0;
}

/**
 * GET SHIPPING OPTIONS: Determines the available shipping options for a given logmat.
 * Uses customer metafields and warehouse data to decide which options to offer.
 */
function getShippingOptionsForGroup(logmatId, logmatTotal) {
  console.log("[getShippingOptionsForGroup] Called with logmatId:", logmatId, "and logmatTotal:", logmatTotal);
  const mf = window.customerMetafields?.custom;
  console.log("[getShippingOptionsForGroup] Customer metafields:", mf);

  // Log and retrieve the closest pickup location.
  const closestPL = mf?.closest_pl || "";
  console.log("[getShippingOptionsForGroup] Customer closest_pl:", closestPL);

  // Retrieve the closest warehouse for this logmat.
  const whKey = mf ? mf[`closest_wh${logmatId}`] : "";
  console.log("[getShippingOptionsForGroup] Customer closest_wh" + logmatId + ":", whKey);

  let options = [];

  // If the warehouse is not valid (i.e. it returns "xx" or an empty string),
  // then always offer Batch Order Shipping regardless of the order total.
  if (!whKey || whKey === "xx") {
    console.log("[getShippingOptionsForGroup] Warehouse not available (xx or empty). Using Batch Order Shipping only.");
    if (closestPL.trim() !== "") {
      options.push({
        value: "local_pickup",
        label: "Store Pickup",
        location: window.pickupLocations?.[closestPL]?.location || "Store",
        address: window.pickupLocations?.[closestPL]?.address || "Address not set",
        hours: window.pickupLocations?.[closestPL]?.hours || "Hours not set"
      });
    }
    options.push({ value: "batch_shipping", label: "Batch Order Shipping", pricePerMile: 2.50 });
    console.log("[getShippingOptionsForGroup] Returning options (warehouse not available):", options);
    return options;
  }

  // If a valid warehouse key is available, then check the warehouse's minimum order.
  let whMinOrder = window.warehouseData?.[whKey]?.minOrder || 0;
  console.log("[getShippingOptionsForGroup] Warehouse minimum order for", whKey + ":", whMinOrder);

  // Build options based on whether a pickup location is available.
  if (closestPL.trim() !== "") {
    // Add local pickup option.
    options.push({
      value: "local_pickup",
      label: "Store Pickup",
      location: window.pickupLocations?.[closestPL]?.location || "Store",
      address: window.pickupLocations?.[closestPL]?.address || "Address not set",
      hours: window.pickupLocations?.[closestPL]?.hours || "Hours not set"
    });
    // If the order meets the warehouse minimum, offer free shipping; otherwise, batch shipping.
    options.push(logmatTotal >= whMinOrder
      ? { value: "free_shipping", label: "Free Shipping", price: 0 }
      : { value: "batch_shipping", label: "Batch Order Shipping", pricePerMile: 2.50 });
  } else {
    options.push(logmatTotal >= whMinOrder
      ? { value: "free_shipping", label: "Free Shipping", price: 0 }
      : { value: "batch_shipping", label: "Batch Order Shipping", pricePerMile: 2.50 });
  }

  console.log("[getShippingOptionsForGroup] Returning options:", options);
  return options;
}



/**
 * BUILD SHIPPING OPTION SNIPPET:
 * Returns the HTML snippet for a shipping option.
 * You can style these snippets externally using the classes prefixed with "cs-".
 */
function buildShippingOptionSnippet(option, groupId) {
  let html = "";
  switch (option.value) {
      case "batch_shipping":
  html = `
    <label class="cs-shipping-option" style="padding: 1rem; border-radius: 8px; background-color: #333; display: flex; flex-direction: column; gap: 4px; cursor: pointer;">
      <div class="cs-shipping-option-header" style="display: flex; align-items: center;">
        <input type="radio" name="shipping-choice-logmat-${groupId}" value="batch_shipping" class="cs-radio" style="margin-right: 8px;" />
        <span class="cs-option-text" style="color: #fff; font-size: 1.1rem; font-weight: 600;">
          Batch Order Shipping
        </span>
      </div>
      <div class="cs-option-price" style="color: #fff; margin-left: 24px; font-size: 0.9rem;">
        Work with our experts after purchase ($${option.pricePerMile.toFixed(2)}/mile)
      </div>
      <a href="#" class="cs-modal-link" data-cs-modal-content="modal-batch-shipping" style="margin-left: 24px; font-size: 0.9rem; text-decoration: underline; color: white;">
        Learn More About Batch Shipping
      </a>
    </label>
  `;
  break;
    case "free_shipping":
      html = `
        <label class="cs-shipping-option" style="padding: 1rem; border-radius: 8px; background-color: #333; display: flex; flex-direction: column; gap: 4px; cursor: pointer;">
          <div class="cs-shipping-option-header" style="display: flex; align-items: center;">
            <input type="radio" name="shipping-choice-logmat-${groupId}" value="free_shipping" class="cs-radio" style="margin-right: 8px;" />
            <span class="cs-option-text" style="color: #fff; font-size: 1.1rem; font-weight: 600;">Free Shipping</span>
          </div>
        </label>
      `;
      break;
    case "local_pickup":
  html = `
    <label class="cs-shipping-option" style="padding: 1rem; border-radius: 8px; background-color: #333; display: flex; flex-direction: column; gap: 4px; cursor: pointer;">
      <div class="cs-shipping-option-header" style="display: flex; align-items: center;">
        <input type="radio" name="shipping-choice-logmat-${groupId}" value="local_pickup" class="cs-radio" style="margin-right: 8px;" />
        <span class="cs-option-text" style="color: #fff; font-size: 1.1rem; font-weight: 600;">
          Store Pickup at 
          <a href="#" class="cs-modal-link" data-cs-modal-content="modal-address-hours" data-value-address="${option.address}" data-value-hours="${option.hours}" data-value-location="${option.location}" style="text-decoration: underline; color: blue;">
            ${option.location}
          </a>
        </span>
      </div>
      <div class="cs-option-subtext" style="margin-left: 24px; font-size: 0.9rem; color: #666;">
        Order now, pickup in 1-3 days
      </div>
    </label>
  `;
  break;
    

    default:
      html = `
        <label class="cs-shipping-option" style="padding: 1rem; border-radius: 8px; background-color: #333; display: flex; flex-direction: column; gap: 4px; cursor: pointer;">
          <div class="cs-shipping-option-header" style="display: flex; align-items: center;">
            <input type="radio" name="shipping-choice-logmat-${groupId}" value="${option.value}" class="cs-radio" style="margin-right: 8px;" />
            <span class="cs-option-text" style="color: #fff; font-size: 1.1rem; font-weight: 600;">${option.label}</span>
          </div>
        </label>
      `;
  }
  return html;
}


/**
 * RENDER RADIO BUTTONS: Inserts the shipping option snippets into the logmat group.
 * Attaches change event listeners to revalidate the ship-confirm button.
 */
function renderRadioButtonsForGroup(group, options) {
  console.log("[renderRadioButtonsForGroup] Called for group:", group, "with options:", options);
  let container = group.querySelector(".shipping-options");
  if (container) {
    console.log("[renderRadioButtonsForGroup] Found existing container; clearing its content.");
    container.innerHTML = "";
  } else {
    console.log("[renderRadioButtonsForGroup] No container found; creating a new container.");
    container = document.createElement("div");
    container.classList.add("shipping-options");
    group.appendChild(container);
  }

  const groupId = group.getAttribute("data-logmat");
  let snippetHTML = "";
  options.forEach(option => {
    snippetHTML += buildShippingOptionSnippet(option, groupId);
  });
  container.innerHTML = snippetHTML;

  // Attach change event listeners for all radios in this group to revalidate selections
  container.querySelectorAll(`input[name="shipping-choice-logmat-${groupId}"]`).forEach(radio => {
    radio.addEventListener("change", function() {
      console.log("[renderRadioButtonsForGroup] Radio changed for group", groupId, "->", this.value);
      validateShippingSelections();
    });
  });

  console.log("[renderRadioButtonsForGroup] Completed rendering options for logmat group:", groupId);
}

/**
 * VALIDATE SHIPPING SELECTIONS: Checks that every logmat group has a selected radio.
 * Enables the #ship-confirm button only if all groups have a selection.
 */
function validateShippingSelections() {
  console.log("[validateShippingSelections] Validating shipping selections for all logmat groups.");
  let valid = true;
  document.querySelectorAll(".logmat-group").forEach(group => {
    const selected = group.querySelector('input[type="radio"]:checked');
    if (!selected) {
      console.log("[validateShippingSelections] No selection in logmat group:", group.getAttribute("data-logmat"));
      valid = false;
    }
  });
  const shipConfirmBtn = document.getElementById("ship-confirm");
  if (shipConfirmBtn) {
    shipConfirmBtn.disabled = !valid;
    console.log("[validateShippingSelections] #ship-confirm disabled set to", !valid);
  } else {
    console.warn("[validateShippingSelections] #ship-confirm button not found.");
  }
}
